import {
    buildInvestmentWaterfall,
    computeAhaMoment,
    initIntakeInvestment,
    toPersistedInvestmentWaterfall,
} from '@fruitful/money-map-simulator';
import {
    type AhaMomentIntake,
    type ChatInvestmentData,
    CMMBranch,
    ExistingAccountType,
    GoalsDebtType,
    type OnboardingChatResults,
} from '@fruitful/types';

/* ─── Preset intakes per branch ─── */

/*
 * Money Map bucket model:
 *   income = bills + spend + goals
 *
 * - monthlyExpensesCents (bills): ALL fixed obligations INCLUDING debt minimums
 * - monthlySpendCents (spend): discretionary spending
 * - monthlyGoalsSpendCents (goals): EXTRA money above minimums toward debt
 *   payoff, savings targets, investing, etc.
 *
 * Debt minimums live in bills, NOT goals. Goals is only the surplus that
 * accelerates payoff beyond minimum payments.
 */

export const SHARED_BASE: Pick<
    AhaMomentIntake,
    | 'monthlyTakeHomeCents'
    | 'monthlyExpensesCents'
    | 'monthlySpendCents'
    | 'monthlyGoalsSpendCents'
> = {
    monthlyTakeHomeCents: 750000,
    monthlyExpensesCents: 350000,
    monthlySpendCents: 150000,
    monthlyGoalsSpendCents: 250000,
};

export const SHARED_INCOMES: AhaMomentIntake['incomes'] = {
    monthlyTakeHomeCents: 750000,
    annualVariableIncomeCents: 0,
    incomeSources: [
        {
            label: 'Salary',
            monthlyCents: 750000,
            cadence: 'biweekly',
            rawAmountCents: 346154,
        },
    ],
};

/**
 * Build a realistic bills breakdown that sums to totalExpensesCents.
 * If debtMinimumsCents > 0, it's added as a line item and the remaining
 * amount is distributed across lifestyle categories.
 */
export function buildBills(
    debtMinimumsCents: number,
    totalExpensesCents = 350000,
) {
    const lifestyleCents = totalExpensesCents - debtMinimumsCents;
    const rent = Math.round(lifestyleCents * 0.57);
    const utilities = Math.round(lifestyleCents * 0.14);
    const phoneInternet = Math.round(lifestyleCents * 0.09);
    const insurance = Math.round(lifestyleCents * 0.13);
    const subscriptions =
        lifestyleCents - rent - utilities - phoneInternet - insurance;

    const bills = [
        { category: 'rent_mortgage', label: 'Rent', amountCents: rent },
        { category: 'utilities', label: 'Utilities', amountCents: utilities },
        {
            category: 'phone_internet',
            label: 'Phone & internet',
            amountCents: phoneInternet,
        },
        { category: 'insurance', label: 'Insurance', amountCents: insurance },
        {
            category: 'subscriptions',
            label: 'Subscriptions',
            amountCents: subscriptions,
        },
    ];

    if (debtMinimumsCents > 0) {
        bills.push({
            category: 'debt_minimums',
            label: 'Debt minimums',
            amountCents: debtMinimumsCents,
        });
    }

    return bills;
}

/**
 * Playground JSON mirrors chat-captured intake. Investment slider defaults
 * (`annualContributionCents`, `projectedYears`) are applied via
 * `initIntakeInvestment` when parsing — keep them out of presets.
 */
export type PlaygroundIntake = Omit<AhaMomentIntake, 'investment'> & {
    investment?: ChatInvestmentData | null;
};

export const BRANCH_PRESETS: Record<string, PlaygroundIntake> = {
    [CMMBranch.GoalsDebt]: {
        ...SHARED_BASE,
        branch: CMMBranch.GoalsDebt,
        incomes: SHARED_INCOMES,
        bills: buildBills(55000),
        debts: [
            {
                description: 'Chase Sapphire',
                minimumPaymentCents: 20000,
                balanceRemainingCents: 1200000,
                annualInterestRatePercent: 21.99,
            },
            {
                description: 'Navient student loan',
                minimumPaymentCents: 35000,
                balanceRemainingCents: 2800000,
                annualInterestRatePercent: 6.8,
            },
        ],
        debtTypes: [GoalsDebtType.CreditCards, GoalsDebtType.StudentLoans],
    },
    GoalsDebt_LowInterest: {
        ...SHARED_BASE,
        branch: CMMBranch.GoalsDebt,
        incomes: SHARED_INCOMES,
        bills: buildBills(74000),
        debts: [
            {
                description: 'Federal student loan',
                minimumPaymentCents: 32000,
                balanceRemainingCents: 3400000,
                annualInterestRatePercent: 4.99,
            },
            {
                description: 'Toyota auto loan',
                minimumPaymentCents: 42000,
                balanceRemainingCents: 1800000,
                annualInterestRatePercent: 5.9,
            },
        ],
        debtTypes: [GoalsDebtType.StudentLoans, GoalsDebtType.CarLoan],
    },
    GoalsDebt_Overwhelming: {
        ...SHARED_BASE,
        monthlyExpensesCents: 500000,
        monthlyGoalsSpendCents: 100000,
        branch: CMMBranch.GoalsDebt,
        incomes: SHARED_INCOMES,
        bills: buildBills(280000, 500000),
        debts: [
            {
                description: 'Chase Sapphire Reserve',
                minimumPaymentCents: 65000,
                balanceRemainingCents: 3200000,
                annualInterestRatePercent: 22.49,
            },
            {
                description: 'Citi Double Cash',
                minimumPaymentCents: 55000,
                balanceRemainingCents: 2800000,
                annualInterestRatePercent: 20.99,
            },
            {
                description: 'Capital One Venture',
                minimumPaymentCents: 72000,
                balanceRemainingCents: 3600000,
                annualInterestRatePercent: 24.99,
            },
            {
                description: 'Discover It',
                minimumPaymentCents: 48000,
                balanceRemainingCents: 2400000,
                annualInterestRatePercent: 26.49,
            },
            {
                description: 'Wells Fargo Active Cash',
                minimumPaymentCents: 40000,
                balanceRemainingCents: 1900000,
                annualInterestRatePercent: 21.24,
            },
        ],
        debtTypes: [GoalsDebtType.CreditCards],
    },
    GoalsDebt_Overwhelming2: {
        monthlyTakeHomeCents: 400000,
        monthlyExpensesCents: 430000,
        monthlySpendCents: 0,
        monthlyGoalsSpendCents: 0,
        branch: CMMBranch.GoalsDebt,
        incomes: {
            monthlyTakeHomeCents: 400000,
            annualVariableIncomeCents: 0,
            incomeSources: [
                {
                    label: 'Hourly job',
                    monthlyCents: 400000,
                    cadence: 'biweekly',
                    rawAmountCents: 184615,
                },
            ],
        },
        bills: buildBills(165000, 430000),
        debts: [
            {
                description: 'Chase Sapphire Reserve',
                minimumPaymentCents: 45000,
                balanceRemainingCents: 2200000,
                annualInterestRatePercent: 22.49,
            },
            {
                description: 'Citi Double Cash',
                minimumPaymentCents: 38000,
                balanceRemainingCents: 1800000,
                annualInterestRatePercent: 20.99,
            },
            {
                description: 'Capital One Venture',
                minimumPaymentCents: 52000,
                balanceRemainingCents: 2600000,
                annualInterestRatePercent: 24.99,
            },
            {
                description: 'Discover It',
                minimumPaymentCents: 30000,
                balanceRemainingCents: 1400000,
                annualInterestRatePercent: 26.49,
            },
        ],
        debtTypes: [GoalsDebtType.CreditCards],
    },
    GoalsDebt_MortgageOnly: {
        ...SHARED_BASE,
        monthlyExpensesCents: 450000,
        monthlyGoalsSpendCents: 150000,
        branch: CMMBranch.GoalsDebt,
        incomes: SHARED_INCOMES,
        bills: buildBills(220000, 450000),
        debts: [
            {
                description: 'Home mortgage',
                minimumPaymentCents: 220000,
                balanceRemainingCents: 32000000,
                annualInterestRatePercent: 6.875,
            },
        ],
        debtTypes: [GoalsDebtType.Mortgage],
    },
    [CMMBranch.GoalsSavings]: {
        ...SHARED_BASE,
        branch: CMMBranch.GoalsSavings,
        incomes: SHARED_INCOMES,
        bills: buildBills(0),
        savings: [
            {
                description: 'Down payment on a house',
                targetAmountCents: 6000000,
                currentAmountCents: 1500000,
            },
        ],
    },
    GoalsSavings_WithDebt: {
        ...SHARED_BASE,
        branch: CMMBranch.GoalsSavings,
        incomes: SHARED_INCOMES,
        bills: buildBills(12000),
        savings: [
            {
                description: 'Wedding',
                targetAmountCents: 2500000,
                currentAmountCents: 800000,
            },
        ],
        debts: [
            {
                description: 'Capital One Quicksilver',
                minimumPaymentCents: 12000,
                balanceRemainingCents: 650000,
                annualInterestRatePercent: 24.99,
            },
        ],
        debtTypes: [GoalsDebtType.CreditCards],
    },
    GoalsSavings_WithLowDebt: {
        ...SHARED_BASE,
        branch: CMMBranch.GoalsSavings,
        incomes: SHARED_INCOMES,
        bills: buildBills(32000),
        savings: [
            {
                description: 'Vacation',
                targetAmountCents: 800000,
                currentAmountCents: 200000,
            },
        ],
        debts: [
            {
                description: 'Federal student loan',
                minimumPaymentCents: 32000,
                balanceRemainingCents: 2400000,
                annualInterestRatePercent: 4.99,
            },
        ],
        debtTypes: [GoalsDebtType.StudentLoans],
    },
    GoalsSavings_WithMixedDebt: {
        ...SHARED_BASE,
        branch: CMMBranch.GoalsSavings,
        incomes: SHARED_INCOMES,
        bills: buildBills(49500),
        savings: [
            {
                description: 'Down payment on a house',
                targetAmountCents: 6000000,
                currentAmountCents: 1500000,
            },
        ],
        debts: [
            {
                description: 'Capital One Quicksilver',
                minimumPaymentCents: 17500,
                balanceRemainingCents: 850000,
                annualInterestRatePercent: 24.99,
            },
            {
                description: 'Federal student loan',
                minimumPaymentCents: 32000,
                balanceRemainingCents: 2500000,
                annualInterestRatePercent: 5.5,
            },
        ],
        debtTypes: [GoalsDebtType.CreditCards, GoalsDebtType.StudentLoans],
    },
    GoalsSavings_Overwhelming: {
        monthlyTakeHomeCents: 400000,
        monthlyExpensesCents: 430000,
        monthlySpendCents: 0,
        monthlyGoalsSpendCents: 0,
        branch: CMMBranch.GoalsSavings,
        incomes: {
            monthlyTakeHomeCents: 400000,
            annualVariableIncomeCents: 0,
            incomeSources: [
                {
                    label: 'Hourly job',
                    monthlyCents: 400000,
                    cadence: 'biweekly',
                    rawAmountCents: 184615,
                },
            ],
        },
        bills: buildBills(165000, 430000),
        savings: [
            {
                description: 'Vacation',
                targetAmountCents: 500000,
                currentAmountCents: 0,
            },
        ],
        debts: [
            {
                description: 'Chase Sapphire Reserve',
                minimumPaymentCents: 45000,
                balanceRemainingCents: 2200000,
                annualInterestRatePercent: 22.49,
            },
            {
                description: 'Citi Double Cash',
                minimumPaymentCents: 38000,
                balanceRemainingCents: 1800000,
                annualInterestRatePercent: 20.99,
            },
            {
                description: 'Capital One Venture',
                minimumPaymentCents: 52000,
                balanceRemainingCents: 2600000,
                annualInterestRatePercent: 24.99,
            },
            {
                description: 'Discover It',
                minimumPaymentCents: 30000,
                balanceRemainingCents: 1400000,
                annualInterestRatePercent: 26.49,
            },
        ],
        debtTypes: [GoalsDebtType.CreditCards],
    },
    [CMMBranch.StartInvesting]: {
        monthlyTakeHomeCents: Math.round((11000000 * 0.7) / 12),
        monthlyExpensesCents: 0,
        monthlySpendCents: 0,
        monthlyGoalsSpendCents: 0,
        branch: CMMBranch.StartInvesting,
        investment: {
            isJoint: false,
            filingStatus: null,
            members: [
                {
                    label: 'Primary member',
                    age: 32,
                },
            ],
            existingAccounts: [],
            annualHouseholdPreTaxIncomeCents: 10000000,
        },
    },
    StartInvesting_FullAccounts: {
        monthlyTakeHomeCents: Math.round((12000000 * 0.7) / 12),
        monthlyExpensesCents: 0,
        monthlySpendCents: 0,
        monthlyGoalsSpendCents: 0,
        branch: CMMBranch.StartInvesting,
        investment: {
            isJoint: false,
            filingStatus: null,
            members: [
                {
                    label: 'Primary member',
                    age: 38,
                },
            ],
            existingAccounts: [
                {
                    accountType: ExistingAccountType.Brokerage,
                    balanceCents: null,
                    ownership: 'unknown',
                },
                {
                    accountType: ExistingAccountType.IRA,
                    balanceCents: 3500000,
                    ownership: 'primary',
                },
                {
                    accountType: ExistingAccountType.RothIRA,
                    balanceCents: 1800000,
                    ownership: 'primary',
                },
                {
                    accountType: ExistingAccountType.EmployerRetirement,
                    balanceCents: null,
                    ownership: 'unknown',
                },
            ],
            annualHouseholdPreTaxIncomeCents: 12000000,
            plannedStartingCashContributionCents: 2000000,
        },
    },
    [CMMBranch.MoneySystem]: {
        ...SHARED_BASE,
        branch: CMMBranch.MoneySystem,
        incomes: SHARED_INCOMES,
        bills: buildBills(0),
        savings: [
            {
                description: 'Annual travel',
                targetAmountCents: 500000,
                currentAmountCents: 0,
            },
        ],
    },
    StartInvesting_Joint: {
        monthlyTakeHomeCents: Math.round((20000000 * 0.7) / 12),
        monthlyExpensesCents: 0,
        monthlySpendCents: 0,
        monthlyGoalsSpendCents: 0,
        branch: CMMBranch.StartInvesting,
        investment: {
            isJoint: true,
            filingStatus: 'married_filing_jointly',
            members: [
                { label: 'Primary member', age: 35 },
                { label: 'Partner member', age: 33 },
            ],
            existingAccounts: [
                {
                    accountType: ExistingAccountType.IRA,
                    balanceCents: 4500000,
                    ownership: 'primary',
                },
                {
                    accountType: ExistingAccountType.RothIRA,
                    balanceCents: 1200000,
                    ownership: 'partner',
                },
            ],
            annualHouseholdPreTaxIncomeCents: 20000000,
        },
    },
    [CMMBranch.GoalsFire]: {
        monthlyTakeHomeCents: Math.round((8000000 * 0.7) / 12),
        monthlyExpensesCents: 0,
        monthlySpendCents: 0,
        monthlyGoalsSpendCents: 0,
        branch: CMMBranch.StartInvesting,
        investment: {
            isJoint: true,
            filingStatus: 'married_filing_separately',
            members: [
                { label: 'Primary member', age: 40 },
                { label: 'Partner member', age: 38 },
            ],
            existingAccounts: [
                {
                    accountType: ExistingAccountType.IRA,
                    balanceCents: 2500000,
                    ownership: 'primary',
                },
            ],
            annualHouseholdPreTaxIncomeCents: 8000000,
        },
    },
    /**
     * Regression: ages 50+ use the IRA catch-up limit, which was once a
     * module-init const derived through a builders ↔ investmentWaterfall
     * circular import and became NaN ($NaN on the waterfall slide).
     */
    StartInvesting_Age50CatchUp: {
        monthlyTakeHomeCents: Math.round((80000000 * 0.7) / 12),
        monthlyExpensesCents: 0,
        monthlySpendCents: 0,
        monthlyGoalsSpendCents: 0,
        branch: CMMBranch.StartInvesting,
        investment: {
            isJoint: true,
            filingStatus: 'single',
            members: [
                { label: 'Primary member', age: 51 },
                { label: 'Partner member', age: 51 },
            ],
            existingAccounts: [
                {
                    accountType: ExistingAccountType.IRA,
                    balanceCents: 0,
                    ownership: 'primary',
                },
                {
                    accountType: ExistingAccountType.RothIRA,
                    balanceCents: null,
                    ownership: 'primary',
                },
                {
                    accountType: ExistingAccountType.IRA,
                    balanceCents: 0,
                    ownership: 'partner',
                },
                {
                    accountType: ExistingAccountType.RothIRA,
                    balanceCents: null,
                    ownership: 'partner',
                },
            ],
            annualHouseholdPreTaxIncomeCents: 80_000_000,
        },
    },
    /**
     * Solo, high income + funded traditional IRA → IRA excluded due to
     * pro-rata rule. Waterfall should show brokerage only (no Roth or
     * Backdoor Roth IRA).
     */
    StartInvesting_TradIraExclusion: {
        monthlyTakeHomeCents: Math.round((20_000_000 * 0.7) / 12),
        monthlyExpensesCents: 0,
        monthlySpendCents: 0,
        monthlyGoalsSpendCents: 0,
        branch: CMMBranch.StartInvesting,
        investment: {
            isJoint: false,
            filingStatus: null,
            members: [{ label: 'Primary member', age: 40 }],
            existingAccounts: [
                {
                    accountType: ExistingAccountType.IRA,
                    balanceCents: 5_000_000,
                    ownership: 'primary',
                },
                {
                    accountType: ExistingAccountType.RothIRA,
                    balanceCents: 2_000_000,
                    ownership: 'primary',
                },
            ],
            annualHouseholdPreTaxIncomeCents: 20_000_000,
            plannedStartingCashContributionCents: 1_000_000,
        },
    },
    /**
     * Joint, high income + both partners have funded traditional IRAs →
     * both excluded from IRA. Waterfall should show brokerage only.
     */
    StartInvesting_JointBothTradIra: {
        monthlyTakeHomeCents: Math.round((30_000_000 * 0.7) / 12),
        monthlyExpensesCents: 0,
        monthlySpendCents: 0,
        monthlyGoalsSpendCents: 0,
        branch: CMMBranch.StartInvesting,
        investment: {
            isJoint: true,
            filingStatus: 'married_filing_jointly',
            members: [
                { label: 'Primary member', age: 42 },
                { label: 'Partner member', age: 39 },
            ],
            existingAccounts: [
                {
                    accountType: ExistingAccountType.IRA,
                    balanceCents: 6_000_000,
                    ownership: 'primary',
                },
                {
                    accountType: ExistingAccountType.IRA,
                    balanceCents: 3_500_000,
                    ownership: 'partner',
                },
            ],
            annualHouseholdPreTaxIncomeCents: 30_000_000,
        },
    },
};

/* ─── Curated scenarios for the dev "canned CMM" signup ─── */

export interface CmmScenario {
    key: string;
    title: string;
    description: string;
    presetKey: string;
}

export const CMM_SCENARIOS: CmmScenario[] = [
    {
        key: 'debt-mixed',
        title: 'Get out of debt: mixed debt',
        description: 'Credit card + student loan, debt-free date result',
        presetKey: CMMBranch.GoalsDebt,
    },
    {
        key: 'debt-overwhelming',
        title: 'Get out of debt: overwhelming',
        description: 'Large debt load, long payoff timeline',
        presetKey: 'GoalsDebt_Overwhelming',
    },
    {
        key: 'savings',
        title: 'Save for something',
        description: 'Savings goal, no debt',
        presetKey: CMMBranch.GoalsSavings,
    },
    {
        key: 'money-system',
        title: 'Create a money system',
        description: 'Money system branch',
        presetKey: CMMBranch.MoneySystem,
    },
    {
        key: 'invest-solo',
        title: 'Optimize my investments: solo',
        description: 'Start investing, single filer',
        presetKey: CMMBranch.StartInvesting,
    },
    {
        key: 'invest-joint',
        title: 'Optimize my investments: joint',
        description: 'Start investing, joint filers',
        presetKey: 'StartInvesting_Joint',
    },
];

/**
 * Builds the same `OnboardingChatResults` the chat's AhaMomentWidget would
 * produce, from a canned intake, without running the conversation.
 */
export function buildCannedResults(presetKey: string): OnboardingChatResults {
    const preset = BRANCH_PRESETS[presetKey];
    if (preset == null) {
        throw new Error(`Unknown CMM preset: ${presetKey}`);
    }
    const { investment, ...rest } = preset;
    const intake: AhaMomentIntake = {
        ...rest,
        ...(investment != null && {
            investment: initIntakeInvestment(investment),
        }),
    };

    const result = computeAhaMoment(intake);
    const investmentWaterfall =
        intake.branch === CMMBranch.StartInvesting && intake.investment != null
            ? toPersistedInvestmentWaterfall(
                  buildInvestmentWaterfall(intake.investment),
              )
            : undefined;

    return {
        moneyMap: {
            incomeTotalCents: result.moneyMap.incomeTotalCents,
            billsTotalCents: result.moneyMap.billsTotalCents,
            spendTotalCents: result.moneyMap.spendTotalCents,
            goalsTotalCents: result.moneyMap.goalsTotalCents,
        },
        artifactData: {
            debtFreeDate: result.date,
            slides: result.slides.join(','),
            image: result.image,
        },
        rawChatResults: intake,
        ...(investmentWaterfall != null && { investmentWaterfall }),
    };
}
